import React from 'react';
import { AnalysisResult } from '../types';

interface TrainingZonesProps {
  result: AnalysisResult;
}

export const TrainingZones: React.FC<TrainingZonesProps> = ({ result }) => {
  const { zones, powerDistribution, velocityLoss, trainingReadiness, classification } = result;
  
  const optimalLoad = powerDistribution?.optimalLoad || 0;
  const speedReserve = trainingReadiness?.speedReserve || 0;
  const totalVelocityLoss = velocityLoss?.totalVelocityLoss || 0;

  const zoneData = [
    {
      name: 'Neuromuscular',
      range: zones.Technique,
      color: 'bg-green-950 border-green-800 text-green-400',
      description: 'Technical refinement, <10% velocity loss',
      load: `${(optimalLoad * 0.1).toFixed(1)} - ${(optimalLoad * 0.3).toFixed(1)} kg`
    },
    {
      name: 'Speed-Strength',
      range: zones.SpeedStrength,
      color: 'bg-cyan-950 border-cyan-800 text-cyan-400',
      description: 'Speed development, 10-25% velocity loss',
      load: `${(optimalLoad * 0.3).toFixed(1)} - ${(optimalLoad * 0.8).toFixed(1)} kg`
    },
    {
      name: 'Power',
      range: zones.Power,
      color: 'bg-theme-accent-soft border-theme-accent-3 text-theme-accent',
      description: 'Power development, 25-40% velocity loss',
      load: `${(optimalLoad * 0.8).toFixed(1)} - ${(optimalLoad * 1.2).toFixed(1)} kg`
    },
    {
      name: 'Strength-Speed',
      range: zones.StrengthSpeed,
      color: 'bg-red-950 border-red-800 text-red-400',
      description: 'Strength development, 40-55% velocity loss',
      load: `${(optimalLoad * 1.5).toFixed(1)} - ${(optimalLoad * 2.0).toFixed(1)} kg`
    }
  ];

  const getPersonalizedRecommendations = () => {
    const recommendations = [];
    
    if (classification?.label === 'Speed-oriented') {
      recommendations.push('Focus on Power and Strength-Speed zones to develop force capacity');
    } else if (classification?.label === 'Force-oriented') {
      recommendations.push('Emphasize Neuromuscular and Speed-Strength zones for velocity development');
    } else {
      recommendations.push('Balanced profile - rotate through all zones systematically');
    }
    
    if (speedReserve > 1.5) {
      recommendations.push('Excellent speed reserve - maintain with neuromuscular work');
    } else if (speedReserve < 0.5) {
      recommendations.push('Low speed reserve - prioritize speed-strength development');
    }
    
    if (totalVelocityLoss > 40) {
      recommendations.push('High velocity loss indicates need for strength-speed work');
    } else if (totalVelocityLoss < 20) {
      recommendations.push('Good load tolerance - can handle higher training volumes');
    }
    
    return recommendations;
  };

  return (
    <div className="bg-theme-surface-1 border border-theme-border rounded-2xl p-6 shadow-card">
      <h3 className="text-2xl font-semibold tracking-tight text-theme-text mb-6">Personalized Training Zones</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {zoneData.map((zone, index) => (
          <div key={index} className={`border rounded-xl p-4 ${zone.color}`}>
            <h4 className="text-lg font-semibold mb-2">{zone.name}</h4>
            <p className="text-lg font-bold mb-1 tabular-nums">{zone.range[0].toFixed(1)} - {zone.range[1].toFixed(1)} kg</p>
            <p className="text-sm font-medium mb-2 tabular-nums">Optimal: {zone.load}</p>
            <p className="text-sm opacity-90">{zone.description}</p>
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-theme-surface-2 border border-theme-border rounded-xl">
          <h4 className="text-lg font-semibold text-theme-text mb-2">Sport-Specific Insights</h4>
          <ul className="text-sm text-theme-muted space-y-1">
            <li>• Speed Reserve: <span className="text-theme-accent font-semibold">{speedReserve.toFixed(1)} m/s</span> {speedReserve > 1.0 ? '(Excellent)' : speedReserve > 0.5 ? '(Good)' : '(Needs Work)'}</li>
            <li>• Load Tolerance: <span className="text-theme-accent font-semibold">{totalVelocityLoss.toFixed(1)}%</span> velocity loss</li>
            <li>• Optimal Power Load: <span className="text-theme-accent font-semibold">{optimalLoad.toFixed(1)} kg</span></li>
          </ul>
        </div>
        
        <div className="p-4 bg-theme-surface-2 border border-theme-border rounded-xl">
          <h4 className="text-lg font-semibold text-theme-text mb-2">Personalized Recommendations</h4>
          <ul className="text-sm text-theme-muted space-y-1">
            {getPersonalizedRecommendations().map((rec, index) => (
              <li key={index}>• {rec}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};